// Talks to /api/v1/admin/partners* — the partner + widget management surface (appDataStore-backed).
// Permission-gated server-side on Admin.Partners. The caller supplies the API bearer + identity hints.
import { authHeaders, type AuthArgs } from './authArgs';

export interface PartnerSummary {
  partnerId: string;
  displayName?: string | null;
  status: string;
  agentCount: number;
  widgetMode?: string | null;
  updatedAt?: string | null;
}

export interface PartnerWidgetConfig {
  mode: string;
  cdnJsUrl?: string | null;
  publishedVersion?: string | null;
  brandingTokens: Record<string, string>;
  overrides: Record<string, string>;
}

export interface PartnerAgentRef {
  agentRef: string;
  kind: string;
  foundryEndpointId?: string | null;
  foundryAgentId?: string | null;
  mcsTokenEndpointUrl?: string | null;
  tags: string[];
}

export interface PartnerRoutingRule {
  condition: string;
  primary: string;
  canary?: { agentRef: string; trafficPct: number } | null;
  fallback?: string | null;
}

export interface PartnerConfig {
  partnerId: string;
  displayName?: string | null;
  status: string;
  onboardedAt?: string | null;
  updatedAt?: string | null;
  widget?: PartnerWidgetConfig | null;
  agents: PartnerAgentRef[];
  routingRules: PartnerRoutingRule[];
}

export interface WidgetAsset {
  path: string;
  length: number;
  contentType?: string | null;
  lastModified?: string | null;
}

export interface WidgetPublishResult {
  partnerId: string;
  version: string;
  cdnJsUrl?: string | null;
}

const base = '/api/v1/admin/partners';

function get(auth: AuthArgs, url: string): Promise<Response> {
  return fetch(url, { method: 'GET', headers: authHeaders(auth) });
}

export async function listPartners(auth: AuthArgs): Promise<PartnerSummary[]> {
  const res = await get(auth, base);
  if (!res.ok) throw new Error(`partners ${res.status}: ${await res.text()}`);
  return (await res.json()) as PartnerSummary[];
}

export async function getPartner(auth: AuthArgs, partnerId: string): Promise<PartnerConfig | null> {
  const res = await get(auth, `${base}/${encodeURIComponent(partnerId)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`partner ${res.status}: ${await res.text()}`);
  return (await res.json()) as PartnerConfig;
}

export async function onboardPartner(
  auth: AuthArgs,
  request: { partnerId: string; displayName?: string; brandingTokens?: Record<string, string> },
): Promise<PartnerConfig> {
  const res = await fetch(base, {
    method: 'POST',
    headers: authHeaders(auth),
    body: JSON.stringify(request),
  });
  if (!res.ok) throw new Error(`onboard ${res.status}: ${await res.text()}`);
  return (await res.json()) as PartnerConfig;
}

export async function savePartner(auth: AuthArgs, partner: PartnerConfig): Promise<PartnerConfig> {
  const res = await fetch(`${base}/${encodeURIComponent(partner.partnerId)}`, {
    method: 'PUT',
    headers: authHeaders(auth),
    body: JSON.stringify(partner),
  });
  if (!res.ok) throw new Error(`save partner ${res.status}: ${await res.text()}`);
  return (await res.json()) as PartnerConfig;
}

export async function deletePartner(auth: AuthArgs, partnerId: string): Promise<void> {
  const res = await fetch(`${base}/${encodeURIComponent(partnerId)}`, {
    method: 'DELETE',
    headers: authHeaders(auth),
  });
  if (!res.ok && res.status !== 204) throw new Error(`delete partner ${res.status}: ${await res.text()}`);
}

export async function listWidgetVersions(auth: AuthArgs, partnerId: string): Promise<string[]> {
  const res = await get(auth, `${base}/${encodeURIComponent(partnerId)}/widget/versions`);
  if (!res.ok) throw new Error(`widget versions ${res.status}: ${await res.text()}`);
  return (await res.json()) as string[];
}

export async function listWidgetAssets(
  auth: AuthArgs,
  partnerId: string,
  version: string,
): Promise<WidgetAsset[]> {
  const res = await get(
    auth,
    `${base}/${encodeURIComponent(partnerId)}/widget/versions/${encodeURIComponent(version)}/assets`,
  );
  if (!res.ok) throw new Error(`widget assets ${res.status}: ${await res.text()}`);
  return (await res.json()) as WidgetAsset[];
}

export async function uploadWidgetAsset(
  auth: AuthArgs,
  partnerId: string,
  version: string,
  file: File,
  path?: string,
): Promise<void> {
  const form = new FormData();
  form.append('file', file);
  const query = path ? `?path=${encodeURIComponent(path)}` : '';
  const res = await fetch(
    `${base}/${encodeURIComponent(partnerId)}/widget/versions/${encodeURIComponent(version)}/assets${query}`,
    // No JSON Content-Type: the browser sets the multipart/form-data boundary for FormData.
    { method: 'PUT', headers: authHeaders(auth, false), body: form },
  );
  if (!res.ok && res.status !== 204) throw new Error(`upload asset ${res.status}: ${await res.text()}`);
}

export async function publishWidget(
  auth: AuthArgs,
  partnerId: string,
  version: string,
  entryFile?: string,
): Promise<WidgetPublishResult> {
  const query = entryFile ? `?entryFile=${encodeURIComponent(entryFile)}` : '';
  const res = await fetch(
    `${base}/${encodeURIComponent(partnerId)}/widget/versions/${encodeURIComponent(version)}/publish${query}`,
    { method: 'POST', headers: authHeaders(auth) },
  );
  if (!res.ok) throw new Error(`publish widget ${res.status}: ${await res.text()}`);
  return (await res.json()) as WidgetPublishResult;
}
